import moment from "moment";

// Register supported locales once. These imports are side-effectful in Moment,
// so we reset locale explicitly below and synchronize it through i18n.
import "moment/locale/pt";
import "moment/locale/es";
import "moment/locale/de";
import "moment/locale/it";
import "moment/locale/fr";
import "moment/locale/hi";
import "moment/locale/he";
import "moment/locale/ar";

const MOMENT_LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: "en",
  pt: "pt",
  es: "es",
  de: "de",
  it: "it",
  fr: "fr",
  hi: "hi",
  he: "he",
  ar: "ar",
};

export const getMomentLocale = (language: string | null | undefined): string => {
  if (!language) return "en";
  const normalized = language.toLowerCase().split("-")[0];
  return MOMENT_LOCALE_BY_LANGUAGE[normalized] ?? "en";
};

export const applyMomentLocale = (language: string | null | undefined): string => {
  const locale = getMomentLocale(language);
  moment.locale(locale);
  return locale;
};

/**
 * Format a date in the locale of an explicitly-passed language, independent of
 * moment's GLOBAL locale.
 *
 * Why this exists: the global locale is updated from i18next's `languageChanged`
 * handler, but react-i18next re-renders subscribed components on that same event.
 * A component that renders before the handler lands formats with the OLD locale,
 * and unless something re-renders it later it stays that way — the scheduler's
 * WhatsApp preview showed an English "August 29, 2026 / 1:18 AM" inside an
 * otherwise fully-Hebrew dialog for exactly this reason. Passing the language in
 * removes the ordering dependency: the output is a pure function of the render's
 * own props.
 */
export const formatInLanguage = (
  value: moment.MomentInput,
  language: string | null | undefined,
  format: string,
): string => moment(value).locale(getMomentLocale(language)).format(format);

/**
 * Relative time ("in 6 hours") in the locale of an explicitly-passed language.
 *
 * The sibling of `formatInLanguage`, and it exists for the same reason: a bare
 * `moment(x).fromNow()` reads moment's GLOBAL locale, so it renders English
 * inside a Hebrew screen whenever the render beats the `languageChanged`
 * handler. The planner sidebar shipped exactly that — an English "in 6 hours"
 * one line above a correctly-Hebrew "10 ספט׳" produced by `formatInLanguage`
 * on the very same moment object.
 */
export const relativeInLanguage = (
  value: moment.MomentInput,
  language: string | null | undefined,
): string => moment(value).locale(getMomentLocale(language)).fromNow();

// Keep a deterministic baseline after locale registration.
applyMomentLocale("en");
